import { type JsonValue, Value } from "@bufbuild/protobuf";
import Database from "@tauri-apps/plugin-sql";
import { List, Map } from "immutable";
import isEmpty from "lodash-es/isEmpty";
import {
	type ActorRefFromLogic,
	assign,
	emit,
	fromPromise,
	sendParent,
	setup,
	waitFor,
} from "xstate";

import { type BaseSpec, type TablePermission, emptyBaseSpec } from "@moment/api-collab/api-types";
import { type QueryPersistence, Store } from "@moment/api-collab/differential";
import {
	type Action,
	hydrateAction,
	rawActionSchema,
} from "@moment/api-collab/differential/QueryEvaluator/Action";
import { emptyQueryEvaluatorState } from "@moment/api-collab/differential/QueryEvaluator/QueryManagerState";
import { errorQueryPersistence } from "@moment/api-collab/differential/Store/QueryPersistence";
import { type SqliteTableDef } from "@moment/api-collab/differential/schema";
import {
	SqlitePersistence,
	type TableDiff,
	computeSchemaDiff,
	queryEvaluatorStateFromSqlite,
	runCreateTableIfNotExists,
	runEnsureBaseMetadataTable,
	runGetAllRecords,
	runGetTableXInfo,
} from "@moment/api-collab/differential/sqlite";
import { roadmapItemUpvotes, roadmapItems } from "@moment/api-collab/differential/testData";
import { type DatabaseTables } from "@moment/api-collab/differential/types";
import { Logger } from "@moment/logging";

import { isDesktopAppMode } from "../../../auth/interop/common";
import { getActiveDocumentBaseFilePath, getBasesDirPath, mkdir } from "../../../data/filesystem";
import {
	RebootContext,
	type RebootInstance,
	RebootStore,
	RebootSubscriber,
	TablePermissionProto,
	TupleProto,
	isRunningInIframe,
	rebootStoreRef,
	storeId,
} from "../../../data/reboot";
import { LOCAL_WORKSPACE_NAME } from "../../../strings";
import { docsApi } from "../../api/docsapi";
import {
	type AuthenticationMachine,
	authenticationActorRef,
	waitForAccessTokenSilently,
} from "../authentication";
import { RebootQueryPersistence, queryEvaluatorStateFromReboot } from "./RebootQueryPersistence";
import { type BaseMachineEventsEmitted } from "./types";

const logger = new Logger("base.machine");
//const debuglog = (_msg: string, ...args: any[]) => console.log(`[base machine] ${_msg}`, ...args);
const debuglog = (_msg: string, ..._args: any[]) => {};

const loadActiveBaseFromDisk = fromPromise<
	{ sqliteDatabase: Database; schemaDiff: Map<string, TableDiff> },
	{ documentId: string; baseSpec: BaseSpec }
>(async ({ input }) => {
	logger.info("Loading base spec from disk", { documentId: input.documentId });
	const basesDir = await getBasesDirPath(input.documentId);
	const activeBasePath = await getActiveDocumentBaseFilePath(input.documentId);
	await mkdir(basesDir, { recursive: true });

	const sqliteDatabase = await Database.load(`sqlite:${activeBasePath}`);
	await runEnsureBaseMetadataTable(sqliteDatabase, input.baseSpec);

	// Create initial tables from base spec
	let schemaDiff = Map<string, TableDiff>();
	for (const [tableName, tableSpec] of Object.entries(input.baseSpec.tables)) {
		const desiredTable: SqliteTableDef = { tableName, columns: tableSpec.columns };
		await runCreateTableIfNotExists(sqliteDatabase, desiredTable, tableSpec.defaultData);

		const currentTableInfo = await runGetTableXInfo(sqliteDatabase, tableName);
		const diff = computeSchemaDiff(currentTableInfo, desiredTable);
		schemaDiff = schemaDiff.set(tableName, diff);
	}

	return { sqliteDatabase: sqliteDatabase, schemaDiff };
});

const loggedIn = (authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>) => {
	return authenticationActorRef?.getSnapshot().matches("authenticated");
};

const loadBaseSpecFromRemote = fromPromise<
	RebootInstance,
	{
		workspaceName: string;
		documentId: string;
		baseSpec: BaseSpec;
		authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
	}
>(async ({ input }) => {
	logger.info("Loading base spec from remote", {
		workspaceName: input.workspaceName,
		documentId: input.documentId,
	});

	return rebootStoreRef({
		workspaceName: input.workspaceName,
		documentId: input.documentId,
		authenticationActorRef: input.authenticationActorRef,
	});
});

const ensureRebootStoreCreated = fromPromise(
	async ({
		input,
	}: {
		input: {
			workspaceName: string;
			documentId: string;
			sqliteDatabase?: Database;
			reboot?: RebootInstance;
			baseSpec: BaseSpec;
			authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
		};
	}) => {
		let reboot =
			input.reboot ??
			rebootStoreRef({
				workspaceName: input.workspaceName,
				documentId: input.documentId,
				authenticationActorRef: input.authenticationActorRef,
			});
		if (!input.sqliteDatabase) {
			logger.error("cannot publish to reboot without a sqliteDatabase to publish from");
			throw new Error("Could not establish connection to collaboration server");
		}

		const baseSpec = input.baseSpec;
		debuglog("publishing baseSpec to reboot store", { baseSpec });

		if (isEmpty(baseSpec.tables)) {
			debuglog("No tables found in base spec, skipping Reboot store creation");
			return;
		}

		const rebootInput = {} as any;
		for (const tableName in baseSpec.tables) {
			const rows = await runGetAllRecords(input.sqliteDatabase, tableName);
			debuglog("found rows for table", { tableName, rows });

			// reboot does not like empty arrays?
			if (isEmpty(rows)) {
				debuglog("No rows found for table, skipping", { tableName });
				continue;
			}

			rebootInput[tableName] = {
				permissions: [
					TablePermissionProto.fromJson({
						value: {
							subject: "group:all",
							is: "reader_all",
						},
					}) as TablePermissionProto,
				],
				initialTableData: rows.map((ri: any) => {
					if (!ri || typeof ri !== "object" || ri["id"] === undefined) {
						throw new Error(
							`Invalid row data for table ${tableName}: ${JSON.stringify(ri)}`
						);
					}
					return new TupleProto({
						id: ri["id"],
						value: Value.fromJson(ri as JsonValue),
						owner: undefined,
					});
				}),
			};
		}

		if (isEmpty(rebootInput)) {
			debuglog("No tables to ensure, skipping Reboot store creation");
			return;
		}

		if (!loggedIn(input.authenticationActorRef)) {
			debuglog("User is not logged in, skipping Reboot store creation");
			return;
		}

		const { context: rebootContext, store: rebootStore } = reboot;
		const resp = await rebootStore.ensureCreated(rebootContext, {
			tables: rebootInput,
		});

		debuglog("Reboot store ensureCreated response", {
			resp,
		});
	}
);

const makeSqlitePersistence = async ({
	sqliteDatabase,
	baseSpec,
}: {
	sqliteDatabase: Database | undefined;
	baseSpec: BaseSpec;
}): Promise<QueryPersistence> => {
	if (!sqliteDatabase) {
		throw new Error("SQLite database undefined");
	}

	try {
		const initialState = await queryEvaluatorStateFromSqlite(sqliteDatabase, baseSpec);
		return new SqlitePersistence(sqliteDatabase, initialState);
	} catch (e) {
		const state = emptyQueryEvaluatorState();
		const error =
			e instanceof Error ? `Error loading from SQLite file: ${e.message}` : "Unknown error";
		return new SqlitePersistence(sqliteDatabase, { ...state, error });
	}
};

const makeRebootPersistence = async ({
	abortController,
	reboot,
	baseSpec,
}: {
	abortController: AbortController;
	reboot: RebootInstance;
	baseSpec: BaseSpec;
}): Promise<QueryPersistence> => {
	try {
		const { context, store } = reboot;

		//await createTestData(reboot);

		const initialState = await queryEvaluatorStateFromReboot(
			context,
			store,
			baseSpec,
			abortController.signal
		);
		const queryPersistence = new RebootQueryPersistence(context, store, initialState);
		logger.info(`reboot connection initialized`, { url: context.url });
		return queryPersistence;
	} catch (error) {
		logger.error("Error creating query persistence", { error });
		if (abortController.signal.aborted) {
			logger.error("Base data fetch timed out");
			throw new Error("Base data fetch timed out");
		}
		const message = error instanceof Error ? error.message : "Unknown error";
		throw new Error(`Failed to create query persistence from Reboot store: ${message}`);
	}
};

const isOwner = async ({
	workspaceName,
	documentId,
	authenticationActorRef,
}: {
	workspaceName?: string;
	documentId: string;
	authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
}): Promise<boolean> => {
	if (!workspaceName || workspaceName === LOCAL_WORKSPACE_NAME) {
		return true;
	}
	if (!loggedIn(authenticationActorRef)) {
		debuglog("User is not logged in, skipping ownership check");
		return false;
	}

	// Authenticate to Reboot
	const token = await waitForAccessTokenSilently(authenticationActorRef);

	debuglog("checking if user is owner of document", {
		workspaceName,
		documentId,
		permission: "own",
	});

	const result = await docsApi.document.permissions.check({
		token,
		workspaceName,
		documentId,
		permission: "own",
	});
	debuglog("checkDocPermission result", {
		result,
	});

	return result;
};

const initializeStore = fromPromise<
	{
		store: Store<DatabaseTables>;
		isStoreOwner: boolean;
	},
	{
		sqliteDatabase?: Database;
		reboot?: RebootInstance;
		localBaseSpecReplica: () => BaseSpec;
		saveBaseSpec: (baseSpec: BaseSpec) => Promise<void>;
		workspaceName?: string;
		documentId: string;
		authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
	}
>(async ({ input }) => {
	const { sqliteDatabase, reboot, localBaseSpecReplica, saveBaseSpec } = input;

	// TODO: pass in resolved ownership check from parent machine
	const isOwnerPromise = isOwner({ ...input });
	const baseSpec = localBaseSpecReplica();

	const makeQueryPersistence = async function (): Promise<QueryPersistence> {
		if (sqliteDatabase) {
			return makeSqlitePersistence({ sqliteDatabase, baseSpec });
		} else if (reboot && Object.keys(baseSpec.tables).length > 0) {
			const abortController = new AbortController();

			const timeout = setTimeout(() => {
				console.error("Base data fetch timed out.. aborting");
				abortController.abort(new Error("Base data fetch timed out"));
			}, 10000);

			try {
				return makeRebootPersistence({
					abortController,
					reboot,
					baseSpec,
				});
			} catch (error) {
				console.error("Error creating query persistence", { error });
				if (abortController.signal.aborted) {
					console.error("Base data fetch timed out");
					throw new Error("Base data fetch timed out");
				}
				const message = error instanceof Error ? error.message : "Unknown error";
				throw new Error(`Failed to create query persistence from Reboot store: ${message}`);
			} finally {
				clearTimeout(timeout);
			}
		}

		throw new Error("No database provided");
	};

	//
	// IMPORTANT: Temporarily, if there is an error creating the `QueryPersistence`, just create a
	// dummy `QueryPersistence` that will throw an error when the user tries to use it.
	//
	// Some day we'll probably want to retry the error, whatever it was, and report the status as we
	// do so.
	//

	const queryPersistenceOrError = await makeQueryPersistence().catch((e: Error) => e);
	let queryPersistence: QueryPersistence =
		queryPersistenceOrError instanceof Error
			? errorQueryPersistence(queryPersistenceOrError)
			: queryPersistenceOrError;

	const isStoreOwner = await isOwnerPromise;
	const baseSpecPersistence = {
		localBaseSpecReplica,
		saveBaseSpec,
	};
	const store = new Store<DatabaseTables>(queryPersistence, baseSpecPersistence, isStoreOwner);

	debuglog("Initialized differential store", { isStoreOwner, store });
	return { store, isStoreOwner };
});

const resetSubscriber = fromPromise<
	{ subscriber?: RebootSubscriber },
	{
		subscriber?: RebootSubscriber;
		reboot?: RebootInstance;
		store: Store<DatabaseTables>;
		baseSpec: BaseSpec;
		sendStreamError: (error: Error) => void;
	}
>(async ({ input }) => {
	const { subscriber, reboot, store, baseSpec, sendStreamError } = input;
	debuglog("resetting reboot subscriber", {
		hasExistingSubscriber: !!subscriber,
		hasRebootConnection: !!reboot,
		isRunningInIframe: isRunningInIframe(),
		isDesktopApp: isDesktopAppMode(),
	});
	subscriber?.cancel();
	const isErrored = store.status && store.status instanceof Error;
	const canSubscribe = reboot && (isRunningInIframe() || isDesktopAppMode());
	if (!isErrored && canSubscribe) {
		debuglog("initializing new reboot subscriber");
		return {
			subscriber: new RebootSubscriber(reboot, store, baseSpec, sendStreamError),
		};
	}
	debuglog("skipping subscriber initialization");
	return { subscriber: undefined };
});

const syncSpecToSqlite = fromPromise<
	void,
	{
		sqliteDatabase: Database;
		baseSpec: BaseSpec;
	}
>(async ({ input }) => {
	const { sqliteDatabase, baseSpec } = input;
	logger.info("Ensuring tables exist for base spec", { baseSpec });

	// Create tables from base spec
	for (const [tableName, tableSpec] of Object.entries(baseSpec.tables)) {
		const desiredTable = { tableName, columns: tableSpec.columns };
		await runCreateTableIfNotExists(sqliteDatabase, desiredTable, tableSpec.defaultData);
	}
});

export type BaseMachine = typeof baseMachine;

export const baseMachine = setup({
	types: {
		context: {} as {
			documentPath?: string;
			workspaceName?: string;
			documentId: string;
			baseSpec: BaseSpec;
			authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;

			schemaDiff: Map<string, TableDiff>;
			sqliteDatabase?: Database;
			reboot?: RebootInstance;
			rebootSubscriber: RebootSubscriber | undefined;

			store?: Store<DatabaseTables>;
			isStoreOwner: boolean;

			error: Error | null;
		},
		input: {} as {
			documentPath?: string;
			workspaceName?: string;
			documentId: string;
			baseSpec?: BaseSpec;
			authenticationActorRef?: ActorRefFromLogic<AuthenticationMachine>;
		},
		emitted: {} as BaseMachineEventsEmitted,
		events: {} as
			| { type: "base.opened" }
			| { type: "base.closed" }
			| { type: "base.specChanged"; payload: { baseSpec: BaseSpec } }
			| { type: "base.streamError"; payload: { error: Error } }
			| { type: "base.error"; payload: { error: Error } }
			| { type: "base.updateWorkspaceName"; workspaceName: string }
			| { type: "base.publish"; workspaceName: string }
			| { type: "authn.succeeded" },
	},
	actors: {
		loadActiveBaseFromDisk,
		loadBaseSpecFromRemote,
		initializeStore,
		resetSubscriber,
		syncSpecToSqlite,
		ensureRebootStoreCreated,
	},
	guards: {
		hasDocumentPath: ({ context }) => Boolean(context.documentPath),
		userIsLoggedIn: ({ context }) => {
			return Boolean(context.authenticationActorRef.getSnapshot().matches("authenticated"));
		},
	},
	actions: {
		clearStore: assign(({ context }) => {
			context.rebootSubscriber?.cancel();
			return {
				schemaDiff: Map(),
				sqliteDatabase: undefined,
				reboot: undefined,
				rebootSubscriber: undefined,

				error: null,
				store: undefined,
				isStoreOwner: false,
			};
		}),
	},
}).createMachine({
	id: "base",
	context: ({ input }) => {
		return {
			documentPath: input.documentPath,
			workspaceName: input.workspaceName,
			documentId: input.documentId,
			baseSpec: input.baseSpec ?? emptyBaseSpec(),
			authenticationActorRef: input.authenticationActorRef ?? authenticationActorRef,

			schemaDiff: Map(),
			sqliteDatabase: undefined,
			reboot: undefined,
			rebootSubscriber: undefined,

			error: null,
			store: undefined,
			isStoreOwner: false,
		};
	},
	initial: "idle",
	entry: ({ context }) => {
		debuglog("Base machine started", {
			documentId: context.documentId,
			workspaceName: context.workspaceName,
		});
	},
	states: {
		idle: {
			entry: ({ context }) => {
				debuglog("Base machine idle", {
					documentId: context.documentId,
					workspaceName: context.workspaceName,
				});
			},
			on: {
				"base.opened": { target: "loading" },
			},
		},
		loading: {
			entry: ({ context }) => {
				logger.debug("Base machine loading", {
					documentId: context.documentId,
					workspaceName: context.workspaceName,
				});
			},
			initial: "loadingSpec",
			states: {
				loadingSpec: {
					always: [
						{ guard: "hasDocumentPath", target: "loadingFromDisk" },
						{ target: "loadingFromRemote" },
					],
				},
				loadingFromDisk: {
					entry: ({ context }) => {
						debuglog("Base machine loading from disk", {
							documentId: context.documentId,
							workspaceName: context.workspaceName,
						});
					},
					invoke: {
						src: "loadActiveBaseFromDisk",
						input: ({ context }) => ({
							documentId: context.documentId,
							baseSpec: context.baseSpec,
						}),
						onDone: {
							actions: assign({
								sqliteDatabase: ({ event }) => event.output.sqliteDatabase,
								schemaDiff: ({ event }) => event.output.schemaDiff,
							}),
							target: "initializingStore",
						},
						onError: {
							actions: assign({ error: ({ event }) => event.error as Error }),
							target: "#base.error",
						},
					},
				},
				loadingFromRemote: {
					entry: ({ context }) => {
						debuglog("Base machine loading from remote", {
							documentId: context.documentId,
							workspaceName: context.workspaceName,
						});
					},
					invoke: {
						src: "loadBaseSpecFromRemote",
						input: ({ context }) => ({
							workspaceName: context.workspaceName!,
							documentId: context.documentId,
							baseSpec: context.baseSpec,
							authenticationActorRef: context.authenticationActorRef,
						}),
						onDone: {
							actions: assign({ reboot: ({ event }) => event.output }),
							target: "initializingStore",
						},
						onError: {
							actions: assign({ error: ({ event }) => event.error as Error }),
							target: "#base.error",
						},
					},
				},
				initializingStore: {
					entry: ({ context }) => {
						debuglog("Base machine initializing store", {
							documentId: context.documentId,
							workspaceName: context.workspaceName,
						});
					},
					invoke: {
						src: "initializeStore",
						input: ({ context, self }) => ({
							sqliteDatabase: context.sqliteDatabase,
							reboot: context.reboot,
							localBaseSpecReplica: () => self.getSnapshot().context.baseSpec,
							saveBaseSpec: async (baseSpec: BaseSpec) => {
								self._parent?.send({
									type: "document.baseSpecChanged",
									payload: { base: baseSpec },
								});
								self.send({ type: "base.specChanged", payload: { baseSpec } });
								await waitFor(self, (snap) => snap.matches("syncingSpecToSqlite"));
								await waitFor(self, (snap) => snap.matches("running"));
							},
							workspaceName: context.workspaceName,
							documentId: context.documentId,
							authenticationActorRef: context.authenticationActorRef,
						}),
						onDone: {
							actions: assign({
								store: ({ event }) => event.output.store,
								isStoreOwner: ({ event }) => event.output.isStoreOwner,
							}),
							target: "subscribingToReboot",
						},
						onError: {
							actions: [
								() =>
									debuglog(`error creating store instance, going to #base.error`),
								assign({ error: ({ event }) => event.error as Error }),
							],
							target: "#base.error",
						},
					},
				},
				subscribingToReboot: {
					entry: () => debuglog(`subscribing to reboot updates`),
					invoke: {
						src: "resetSubscriber",
						input: ({ context, self }) => ({
							subscriber: context.rebootSubscriber,
							reboot: context.reboot,
							store: context.store!,
							baseSpec: context.baseSpec,
							sendStreamError: (error: Error) => {
								self.send({ type: "base.streamError", payload: { error } });
							},
						}),
						onDone: {
							actions: assign({
								rebootSubscriber: ({ event }) => event.output.subscriber,
							}),
							target: "#base.running",
						},
						onError: {
							actions: assign({ error: ({ event }) => event.error as Error }),
							target: "#base.error",
						},
					},
				},
			},
		},
		running: {
			entry: [
				({ context }) => {
					debuglog("Base machine running", {
						documentId: context.documentId,
						workspaceName: context.workspaceName,
					});
				},
				sendParent(() => ({
					type: "base.ready",
				})),
			],
			on: {
				"base.specChanged": {
					actions: assign({
						baseSpec: ({ event }) => event.payload.baseSpec,
					}),
					target: "syncingSpecToSqlite",
				},
				"base.closed": {
					actions: "clearStore",
					target: "idle",
				},
				"base.updateWorkspaceName": {
					actions: assign({
						workspaceName: ({ event }) => event.workspaceName,
					}),
				},
				"base.publish": {
					actions: assign({
						workspaceName: ({ event }) => event.workspaceName,
					}),
					target: "ensuringRebootStoreCreated",
				},
				"base.streamError": {
					target: "loading.subscribingToReboot",
				},
				"authn.succeeded": {
					target: "loading",
				},
			},
		},
		// FIXME: this state is parallel to running, which means that events that might
		// be emitted during the running state will be ignored if a sync is in progress.
		// Perhaps that's intentional, to avoid race conditions? If so, we should queue them.
		syncingSpecToSqlite: {
			entry: () => debuglog(`attempting sync to sqlite`),
			invoke: {
				src: "syncSpecToSqlite",
				input: ({ context }) => ({
					sqliteDatabase: context.sqliteDatabase!,
					baseSpec: context.baseSpec,
					isStoreOwner: context.isStoreOwner,
				}),
				onDone: { target: "running" },
				onError: {
					actions: assign({ error: ({ event }) => event.error as Error }),
					target: "#base.error",
				},
			},
		},
		// FIXME: this state is parallel to running, which means that events that might
		// be emitted during the running state will be ignored if a sync is in progress.
		// Perhaps that's intentional, to avoid race conditions? If so, we should queue them.
		ensuringRebootStoreCreated: {
			entry: ({ context }) => {
				debuglog("attempting to call ensureCreated in reboot", {
					workspaceName: context.workspaceName,
					documentId: context.documentId,
				});
			},
			invoke: {
				src: "ensureRebootStoreCreated",
				guard: "userIsLoggedIn",
				input: ({ context }) => ({
					workspaceName: context.workspaceName!,
					documentId: context.documentId,
					sqliteDatabase: context.sqliteDatabase!,
					reboot: context.reboot,
					baseSpec: context.baseSpec,
					authenticationActorRef: context.authenticationActorRef,
				}),
				onDone: {
					actions: emit({
						type: "base.publishedToReboot",
					}),
					target: "running",
				},
				onError: {
					actions: [
						assign({ error: ({ event }) => event.error as Error }),
						emit(({ event }) => ({
							type: "base.publishedToRebootError",
							payload: { error: event.error as Error },
						})),
					],
					target: "#base.error",
				},
			},
		},

		error: {
			entry: [
				({ context }) => {
					logger.error("Base machine error state entered", {
						error: context.error,
						documentId: context.documentId,
						workspaceName: context.workspaceName,
					});
				},
				sendParent(({ context }) => ({
					type: "base.error",
					payload: { error: context.error },
				})),
				({ context }) => context.rebootSubscriber?.cancel(),
			],
			on: { "base.opened": { target: "loading" } },
		},
	},
});

const createTestData = async (reboot: RebootInstance) => {
	await reboot.store?.ensureCreated(reboot.context, {
		tables: {
			roadmapItems: {
				permissions: [
					new TablePermissionProto({
						value: Value.fromJson({
							subject: "group:all",
							is: "reader_all",
						} satisfies TablePermission),
					}),
				],
				initialTableData: roadmapItems.map((ri) => {
					return new TupleProto({
						id: ri.id,
						value: Value.fromJson(ri as unknown as JsonValue),
						owner: undefined,
					});
				}),
			},
			roadmapItemUpvotes: {
				permissions: [
					new TablePermissionProto({
						value: Value.fromJson({
							subject: "group:all",
							is: "writer_own",
						} satisfies TablePermission),
					}),
				],
				initialTableData: roadmapItemUpvotes.map((riu) => {
					return new TupleProto({
						id: riu.id,
						value: Value.fromJson(riu as unknown as JsonValue),
						owner: undefined,
					});
				}),
			},
		},
	});
};
